import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';

/**
 * ADR-015 Fase 2.1: items abertos (pendente | em_andamento) por oferta para o Dashboard.
 * Retorna Map<ofertaId, {
 *   total, pendentes, em_andamento,
 *   latest: { acao, prazo_sugerido, prioridade, autor_tipo, autor_id, status, created_at }
 * }>
 *
 * "latest" = item principal a exibir na coluna Proximo Passo do Dashboard.
 * Criterio composto (mais urgente primeiro):
 *   1. em_andamento > pendente
 *   2. prioridade alta > media > baixa
 *   3. created_at desc (desempate)
 */

const PRIORIDADE_RANK = { alta: 0, media: 1, baixa: 2 };
const STATUS_RANK = { em_andamento: 0, pendente: 1 };

const isMaisUrgente = (a, b) => {
  // retorna true se a deve aparecer antes de b
  const sa = STATUS_RANK[a.status] ?? 99;
  const sb = STATUS_RANK[b.status] ?? 99;
  if (sa !== sb) return sa < sb;
  const pa = PRIORIDADE_RANK[a.prioridade] ?? 99;
  const pb = PRIORIDADE_RANK[b.prioridade] ?? 99;
  if (pa !== pb) return pa < pb;
  // created_at desc
  return (a.created_at || '') > (b.created_at || '');
};

export const useProximoPassoCounts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('proximo_passo_items')
      .select('oferta_id, acao, prazo_sugerido, prioridade, autor_tipo, autor_id, status, created_at')
      .in('status', ['pendente', 'em_andamento']);
    if (!error) setRows(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const byOferta = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const cur = m.get(r.oferta_id);
      if (!cur) {
        m.set(r.oferta_id, {
          total: 1,
          pendentes: r.status === 'pendente' ? 1 : 0,
          em_andamento: r.status === 'em_andamento' ? 1 : 0,
          latest: r,
        });
      } else {
        cur.total += 1;
        if (r.status === 'pendente') cur.pendentes += 1;
        else if (r.status === 'em_andamento') cur.em_andamento += 1;
        if (isMaisUrgente(r, cur.latest)) cur.latest = r;
      }
    }
    return m;
  }, [rows]);

  return { byOferta, loading, refetch: fetchAll };
};

export default useProximoPassoCounts;
